import { CommonModule } from '@angular/common';
import {
  Component,
  EventEmitter,
  inject,
  OnInit,
  ChangeDetectorRef,
  Output,
  Input,
} from '@angular/core';
import { select } from '@ngneat/elf';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { distinctUntilChanged, take, tap, zip } from 'rxjs';

import { Translations } from '@digital-platform/reporting/domain';
import {
  WorkspaceStore,
  ReportDataStore,
  TranslationLoaderService,
} from '@digital-platform/reporting/services';
import {
  CategoryListNavComponent,
  Dropdown,
  Item,
  NavData,
  NavSettings,
} from '@digital-platform/ui-base-components/category-list-nav';

@Component({
  selector: 'digital-platform-report-tracker',
  standalone: true,
  imports: [CommonModule, CategoryListNavComponent],
  templateUrl: './report-tracker.component.html',
  styleUrls: ['./report-tracker.component.scss'],
})
@UntilDestroy()
export class ReportTrackerComponent implements OnInit {
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly workspaceStore = inject(WorkspaceStore);
  private readonly reportDataStore = inject(ReportDataStore);
  private readonly translationService = inject(TranslationLoaderService);

  @Input() data: NavData = [] as NavData;
  @Input() settings: NavSettings = {} as NavSettings;

  @Input() embeddedPageMode = false;
  @Input() activeReportId: string | undefined;
  @Input() id?: string;

  private _favorites: Item[] = [];
  translations: Translations = {};

  @Input()
  get favorites(): Item[] {
    return this._favorites;
  }
  set favorites(value: Item[]) {
    this._favorites = value;
    this.cdr.detectChanges(); // Ensure change detection is triggered
  }

  @Output() itemClick = new EventEmitter<Item>();
  @Output() itemInfoClick = new EventEmitter<Item>();
  @Output() dropdownClick = new EventEmitter<Dropdown>();
  @Output() favoriteItemRemoved = new EventEmitter<Item>();
  @Output() favoritesChanged = new EventEmitter<Item>();

  ngOnInit(): void {
    // Initialize nav data & settings when workspaces load
    zip(
      this.workspaceStore.state$.pipe(take(1)),
      this.translationService.getTranslation('en', 'REPORTING.').pipe(take(1))
    ).subscribe(([state, translations]) => {
      this.reportDataStore.initialize(
        state.workspaces,
        this.embeddedPageMode,
        this.activeReportId,
        translations
      );
    });

    // Subscribe to nav data
    this.reportDataStore.state$
      .pipe(
        select((s) => s.data),
        distinctUntilChanged(),
        tap((d) => {
          this.data = d;
          this.cdr.detectChanges();
        }),
        untilDestroyed(this)
      )
      .subscribe();

    // Subscribe to nav settings
    this.reportDataStore.state$
      .pipe(
        select((s) => s.settings),
        distinctUntilChanged(),
        tap((s) => {
          this.settings = s;
          this.settings.id = this.id ?? 'report-tracker';
          this.cdr.detectChanges();
        }),
        untilDestroyed(this)
      )
      .subscribe();
  }

  handleItemClick(item: Item) {
    // ▶ delegate active‐flag logic to the store
    this.reportDataStore.activateItem(item, this.embeddedPageMode);
    this.itemClick.emit(item);
  }

  handleItemInfoClick(item: Item) {
    this.itemInfoClick.emit(item);
  }

  handleDropdownClick(dropdown: Dropdown) {
    this.reportDataStore.setDropdown(dropdown);
    this.dropdownClick.emit(dropdown);
  }

  handleFavoriteItemRemoved(item: Item) {
    this.favoriteItemRemoved.emit(item);
  }

  handleFavoritesChanged(item: Item) {
    this.favoritesChanged.emit(item);
  }

  addFavoriteItem(item: Item) {
    this.favorites.push(item);
    this.cdr.detectChanges(); // Ensure change detection is triggered
  }
}
